import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PeopleOutlined, ViewCarouselOutlined, GridViewOutlined } from '@mui/icons-material';
import { SPEAKERS_COLOR } from '../constants';
import { InspectorSection, StyleRow, PxInput, ColorInput, StyleSelect, PanelHeader } from '../ui/primitives';

// ─── Breakpoint-aware helpers ─────────────────────────────────────────────────
// Desktop values live directly on el (el.perSlide, el.columns, el.gap …).
// Tablet/mobile overrides live in el.responsive.tablet.* / el.responsive.mobile.*
// This mirrors exactly how getEffectiveStyle works for style-based props.

function getSpkStyle(el, bp) {
  if (!bp || bp === 'desktop') return el;
  // merge desktop base with bp override so reads always have a value
  return { ...el, ...(el.responsive?.[bp] || {}) };
}

function updateSpkProp(el, bp, key, val) {
  if (!bp || bp === 'desktop') {
    return { ...el, [key]: val };
  }
  return {
    ...el,
    responsive: {
      ...(el.responsive || {}),
      [bp]: { ...(el.responsive?.[bp] || {}), [key]: val },
    },
  };
}

// ─── Template preview thumbnails ─────────────────────────────────────────────
function TemplateThumb({ n, active, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
      p: '8px 4px', borderRadius: 1.5, cursor: 'pointer',
      border: `1.5px solid ${active ? SPEAKERS_COLOR : '#e4e9f0'}`,
      background: active ? alpha(SPEAKERS_COLOR, 0.06) : '#f8f9fb',
      transition: 'all 0.15s',
      '&:hover': { borderColor: SPEAKERS_COLOR },
    }}>
      <Box sx={{ width: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
        {n === 1 && (
          <Box sx={{ width: 36, height: 44, borderRadius: '5px', background: 'linear-gradient(180deg, #d4dff0 0%, #8fa8cc 100%)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }} />
            <Box sx={{ position: 'absolute', bottom: 3, left: 3, width: 20, height: 3, borderRadius: 1, background: '#fff', opacity: 0.9 }} />
            <Box sx={{ position: 'absolute', bottom: 7, left: 3, width: 14, height: 2, borderRadius: 1, background: '#fff', opacity: 0.6 }} />
          </Box>
        )}
        {n === 2 && (
          <>
            <Box sx={{ width: 36, height: 36, borderRadius: '5px', background: 'linear-gradient(180deg, #d4dff0 0%, #8fa8cc 100%)' }} />
            <Box sx={{ width: 20, height: 3, borderRadius: 1, background: '#2c3444', opacity: 0.8 }} />
            <Box sx={{ width: 14, height: 2, borderRadius: 1, background: '#9ea8b3', opacity: 0.8 }} />
          </>
        )}
        {n === 3 && (
          <>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(145deg, #d4dff0, #8fa8cc)', border: '2px solid rgba(255,255,255,0.8)' }} />
            <Box sx={{ width: 20, height: 3, borderRadius: 1, background: '#2c3444', opacity: 0.8 }} />
            <Box sx={{ width: 14, height: 2, borderRadius: 1, background: '#9ea8b3', opacity: 0.8 }} />
          </>
        )}
      </Box>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: active ? 700 : 500, color: active ? SPEAKERS_COLOR : '#7a8494', mt: 0.25 }}>
        T{n}
      </Typography>
    </Box>
  );
}

// ─── Mode toggle button ───────────────────────────────────────────────────────
function ModeBtn({ icon, label, active, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
      py: 1, borderRadius: 1.5, cursor: 'pointer',
      border: `1.5px solid ${active ? SPEAKERS_COLOR : '#e4e9f0'}`,
      background: active ? alpha(SPEAKERS_COLOR, 0.07) : '#f8f9fb',
      color: active ? SPEAKERS_COLOR : '#7a8494',
      transition: 'all 0.15s',
      '&:hover': { borderColor: SPEAKERS_COLOR, color: SPEAKERS_COLOR },
    }}>
      {icon}
      <Typography sx={{ fontSize: '0.67rem', fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}

// ─── SpeakersInspector ────────────────────────────────────────────────────────
export function SpeakersInspector({ el, onUpdate, onClose, breakpoint = 'desktop' }) {
  const bp = breakpoint;

  // Merged view of el for the current breakpoint (fallback to desktop for reads)
  const s = getSpkStyle(el, bp);

  // Write a bp-aware prop
  const update = (key, val) => onUpdate(updateSpkProp(el, bp, key, val));

  // Global props that never change per breakpoint — always write directly to el
  const updateGlobal = (key, val) => onUpdate({ ...el, [key]: val });

  const isCarousel = (el.displayMode || 'carousel') === 'carousel';

  const gridJustifyOptions = [
    { value: 'flex-start', label: 'Left' },
    { value: 'center',     label: 'Center' },
    { value: 'flex-end',   label: 'Right' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        icon={<PeopleOutlined sx={{ fontSize: 14 }} />}
        label="Speakers"
        color={SPEAKERS_COLOR}
        onClose={onClose}
        breakpoint={bp}
      />

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Template ── */}
        <InspectorSection label="Template">
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {[1, 2, 3].map(n => (
              <TemplateThumb key={n} n={n} active={(el.template || 1) === n} onClick={() => updateGlobal('template', n)} />
            ))}
          </Box>
          <Box sx={{ mt: 0.75 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', lineHeight: 1.4 }}>
              {(el.template || 1) === 1 && 'Name overlaid on photo with dark scrim'}
              {(el.template || 1) === 2 && 'Rectangular photo with name & title below'}
              {(el.template || 1) === 3 && 'Circle avatar with name & title below'}
            </Typography>
          </Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Display mode ── */}
        <InspectorSection label="Display">
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ModeBtn
              icon={<ViewCarouselOutlined sx={{ fontSize: 18 }} />}
              label="Carousel" active={isCarousel}
              onClick={() => updateGlobal('displayMode', 'carousel')}
            />
            <ModeBtn
              icon={<GridViewOutlined sx={{ fontSize: 18 }} />}
              label="Grid" active={!isCarousel}
              onClick={() => updateGlobal('displayMode', 'grid')}
            />
          </Box>
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Carousel options ── */}
        {isCarousel && (
          <>
            <InspectorSection label="Carousel">
              <StyleRow label="Per slide">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="range" min={1} max={10} step={1}
                    value={s.perSlide ?? 4}
                    onChange={e => update('perSlide', Number(e.target.value))}
                    style={{ flex: 1, accentColor: SPEAKERS_COLOR }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: SPEAKERS_COLOR, minWidth: 18, textAlign: 'right' }}>
                    {s.perSlide ?? 4}
                  </Typography>
                </Box>
              </StyleRow>
              <StyleRow label="Auto scroll">
                <Box
                  onClick={() => updateGlobal('autoScroll', !el.autoScroll)}
                  sx={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', position: 'relative', background: el.autoScroll ? SPEAKERS_COLOR : '#d1d9e0', transition: 'background 0.2s', flexShrink: 0 }}>
                  <Box sx={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s', left: el.autoScroll ? 18 : 2 }} />
                </Box>
              </StyleRow>
              {el.autoScroll && (
                <Box sx={{ px: 0.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', lineHeight: 1.4 }}>
                    Slides every 3 seconds. Pauses on manual navigation then resumes.
                  </Typography>
                </Box>
              )}
            </InspectorSection>
            <Divider sx={{ borderColor: '#f0f2f5' }} />
          </>
        )}

        {/* ── Grid options ── */}
        {!isCarousel && (
          <>
            <InspectorSection label="Grid">
              <StyleRow label="Columns">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="range" min={1} max={8} step={1}
                    value={s.columns ?? 4}
                    onChange={e => update('columns', Number(e.target.value))}
                    style={{ flex: 1, accentColor: SPEAKERS_COLOR }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: SPEAKERS_COLOR, minWidth: 18, textAlign: 'right' }}>
                    {s.columns ?? 4}
                  </Typography>
                </Box>
              </StyleRow>
              <StyleRow label="Justify">
                <StyleSelect
                  value={s.gridJustify ?? 'center'}
                  onChange={v => update('gridJustify', v)}
                  options={gridJustifyOptions}
                />
              </StyleRow>
            </InspectorSection>
            <Divider sx={{ borderColor: '#f0f2f5' }} />
          </>
        )}

        {/* ── Spacing ── */}
        <InspectorSection label="Spacing">
          <StyleRow label="Gap">
            <PxInput value={s.gap} onChange={v => update('gap', v)} placeholder="16" />
          </StyleRow>
          {(el.template || 1) !== 3 && (
            <StyleRow label="Radius">
              <PxInput value={el.cardRadius} onChange={v => updateGlobal('cardRadius', v)} placeholder="12" />
            </StyleRow>
          )}
        </InspectorSection>

        <Divider sx={{ borderColor: '#f0f2f5' }} />

        {/* ── Typography ── */}
        <InspectorSection label="Typography">
          <StyleRow label="Align">
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {['left', 'center', 'right'].map(a => (
                <Box key={a} onClick={() => updateGlobal('textAlign', a)}
                  sx={{
                    flex: 1, py: 0.6, borderRadius: 1, cursor: 'pointer', textAlign: 'center',
                    border: `1.5px solid ${(el.textAlign || 'left') === a ? SPEAKERS_COLOR : '#e4e9f0'}`,
                    background: (el.textAlign || 'left') === a ? alpha(SPEAKERS_COLOR, 0.07) : '#f8f9fb',
                    color: (el.textAlign || 'left') === a ? SPEAKERS_COLOR : '#7a8494',
                    fontSize: '0.67rem', fontWeight: 700, textTransform: 'capitalize',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: SPEAKERS_COLOR, color: SPEAKERS_COLOR },
                  }}>
                  {a}
                </Box>
              ))}
            </Box>
          </StyleRow>
          <StyleRow label="Name size">
            <PxInput value={el.nameFontSize} onChange={v => updateGlobal('nameFontSize', v)} placeholder="15" />
          </StyleRow>
          <StyleRow label="Title size">
            <PxInput value={el.titleFontSize} onChange={v => updateGlobal('titleFontSize', v)} placeholder="13" />
          </StyleRow>
          {(el.template || 1) !== 1 && (
            <>
              <StyleRow label="Name color">
                <ColorInput value={el.nameColor || '#1a1a2e'} onChange={v => updateGlobal('nameColor', v)} />
              </StyleRow>
              <StyleRow label="Title color">
                <ColorInput value={el.titleColor || '#5a6474'} onChange={v => updateGlobal('titleColor', v)} />
              </StyleRow>
            </>
          )}
        </InspectorSection>

      </Box>
    </Box>
  );
}
